import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Activity, AlertTriangle } from 'lucide-react';
import AppLayout from '@/components/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';

// Queue Monitor Content
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PerformanceMonitor } from "@/components/drone/PerformanceMonitor";
import { LoadTester } from "@/components/drone/LoadTester";
import { CheckCircle2, TrendingUp } from "lucide-react";
import { useQueueHealthMonitor } from "@/hooks/useQueueHealthMonitor";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Error Monitor Content
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { XCircle, Clock, CheckCircle2 as CheckIcon, RefreshCw, Download } from 'lucide-react';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

interface SystemError {
  id: string;
  error_type: string;
  severity: string;
  source: string;
  error_message: string;
  error_details: any;
  user_id: string | null;
  org_id: string | null;
  occurred_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  notes: string | null;
  status: string;
}

const ERROR_TYPE_COLORS = {
  database: '#8884d8',
  edge_function: '#82ca9d',
  queue: '#ffc658',
  client: '#ff8042',
  ai_analysis: '#00C49F',
  workflow: '#FFBB28',
  drone: '#FF8042',
};

function QueueMonitorTab() {
  const health = useQueueHealthMonitor();

  const getHealthBadge = () => {
    switch (health.status) {
      case 'healthy':
        return <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />Healthy</Badge>;
      case 'degraded':
        return <Badge variant="secondary"><TrendingUp className="w-3 h-3 mr-1" />Degraded</Badge>;
      case 'critical':
        return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Critical</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground">
            Real-time monitoring and testing for the AI analysis queue system
          </p>
        </div>
        {getHealthBadge()}
      </div>

      {health.status !== 'healthy' && (
        <Alert variant={health.status === 'critical' ? 'destructive' : 'default'}>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Queue {health.status === 'critical' ? 'Critical' : 'Degraded'}:</strong> {' '}
            {health.pendingCount} pending jobs, oldest is {Math.round(health.oldestPendingAge / 1000)}s old, 
            {health.recentFailureRate.toFixed(1)}% recent failure rate
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{health.pendingCount}</div>
            <div className="text-xs text-muted-foreground">Pending Jobs</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{health.processingCount}</div>
            <div className="text-xs text-muted-foreground">Processing Jobs</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{Math.round(health.avgProcessingTime)}ms</div>
            <div className="text-xs text-muted-foreground">Avg Processing Time</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{health.recentFailureRate.toFixed(1)}%</div>
            <div className="text-xs text-muted-foreground">Failure Rate (1h)</div>
          </CardContent>
        </Card>
        <Card className="border-primary/20">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-primary">{health.avgPriority.toFixed(1)}</div>
            <div className="text-xs text-muted-foreground">Avg Priority (1=High)</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cache Performance</CardTitle>
          <CardDescription>
            Intelligent caching reduces API costs and speeds up repeated analyses
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">How It Works</div>
              <ul className="text-sm space-y-1">
                <li>✅ Check cache BEFORE AI call</li>
                <li>✅ SHA-256 image hashing</li>
                <li>✅ 24-hour TTL per result</li>
                <li>✅ Auto-cleanup expired entries</li>
              </ul>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Cost Savings</div>
              <ul className="text-sm space-y-1">
                <li>💰 Skip AI calls for duplicates</li>
                <li>💰 30-60% API cost reduction</li>
                <li>💰 ~$0.01-0.05 saved per hit</li>
                <li>💰 Scales with volume</li>
              </ul>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-muted-foreground">Performance</div>
              <ul className="text-sm space-y-1">
                <li>⚡ 300x faster (100ms vs 30s)</li>
                <li>⚡ Lower queue congestion</li>
                <li>⚡ Better UX</li>
                <li>⚡ Hit count tracking</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <PerformanceMonitor />

      <Card className="border-orange-500/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Load Testing
            <Badge variant="outline" className="text-xs">Submission Test Only</Badge>
          </CardTitle>
          <CardDescription>
            Tests queue submission performance. Check "Performance Monitor" above for actual processing results.
            <br />
            <strong>Note:</strong> If AI vision model is offline, jobs will submit successfully but fail during processing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <LoadTester />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>System Architecture & Stability Features</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Queue Processing</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✅ Priority-based FIFO</li>
                <li>✅ Atomic job claiming</li>
                <li>✅ Worker crash recovery</li>
                <li>✅ Auto job expiration</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Rate Limiting</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✅ Per-org tier limits</li>
                <li>✅ Graceful backpressure</li>
                <li>✅ Queue depth controls</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">Health Monitoring</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✅ Real-time metrics</li>
                <li>✅ Alert thresholds</li>
                <li>✅ Failure tracking</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ErrorMonitorTab() {
  const [errors, setErrors] = useState<SystemError[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d'>('24h');

  useEffect(() => {
    fetchErrors();
    
    const channel = supabase
      .channel('system-errors')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_errors',
        },
        () => {
          fetchErrors();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [timeRange]);

  const fetchErrors = async () => {
    try {
      const cutoff = new Date();
      if (timeRange === '1h') cutoff.setHours(cutoff.getHours() - 1);
      if (timeRange === '24h') cutoff.setHours(cutoff.getHours() - 24);
      if (timeRange === '7d') cutoff.setDate(cutoff.getDate() - 7);

      const { data, error } = await supabase
        .from('system_errors')
        .select('*')
        .gte('occurred_at', cutoff.toISOString())
        .order('occurred_at', { ascending: false });

      if (error) throw error;
      setErrors(data || []);
    } catch (error) {
      console.error('Failed to fetch errors:', error);
      toast.error('Failed to load errors');
    } finally {
      setLoading(false);
    }
  };

  const markAsResolved = async (id: string) => {
    try {
      const { error } = await supabase
        .from('system_errors')
        .update({ 
          status: 'resolved', 
          resolved_at: new Date().toISOString() 
        })
        .eq('id', id);

      if (error) throw error;
      toast.success('Error marked as resolved');
      fetchErrors();
    } catch (error) {
      toast.error('Failed to update error');
    }
  };

  const filteredErrors = filter === 'all' 
    ? errors 
    : errors.filter(e => e.status === filter);

  const criticalErrors = errors.filter(e => 
    e.severity === 'FATAL' || e.severity === 'PANIC'
  ).filter(e => e.status !== 'resolved');

  const errorsByType = Object.entries(
    errors.reduce((acc, e) => {
      acc[e.error_type] = (acc[e.error_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  ).map(([name, value]) => ({ name, value }));

  const errorsByHour = errors.reduce((acc, e) => {
    const hour = new Date(e.occurred_at).getHours();
    acc[hour] = (acc[hour] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);

  const hourlyData = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    errors: errorsByHour[i] || 0,
  }));

  const topErrors = Object.entries(
    errors.reduce((acc, e) => {
      const key = `${e.source}: ${e.error_message.substring(0, 50)}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, count]) => ({ name, count }));

  return (
    <div className="space-y-6">
      {criticalErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{criticalErrors.length} Critical Error{criticalErrors.length > 1 ? 's' : ''}</strong>
            {' '}requires immediate attention
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Errors</p>
              <p className="text-2xl font-bold">{errors.length}</p>
            </div>
            <XCircle className="h-8 w-8 text-destructive" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Critical</p>
              <p className="text-2xl font-bold text-destructive">{criticalErrors.length}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold">{errors.filter(e => e.status === 'new').length}</p>
            </div>
            <Clock className="h-8 w-8 text-warning" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Resolved</p>
              <p className="text-2xl font-bold">{errors.filter(e => e.status === 'resolved').length}</p>
            </div>
            <CheckIcon className="h-8 w-8 text-success" />
          </div>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Errors by Hour</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="errors" stroke="hsl(var(--destructive))" />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Errors by Type</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={errorsByType}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => entry.name}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {errorsByType.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={ERROR_TYPE_COLORS[entry.name as keyof typeof ERROR_TYPE_COLORS]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-4">Most Frequent Errors</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={topErrors} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="name" type="category" width={200} />
            <Tooltip />
            <Bar dataKey="count" fill="hsl(var(--destructive))" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card>
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Error Log</h2>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchErrors()}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              All ({errors.length})
            </Button>
            <Button
              variant={filter === 'new' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('new')}
            >
              Active ({errors.filter(e => e.status === 'new').length})
            </Button>
            <Button
              variant={filter === 'resolved' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('resolved')}
            >
              Resolved ({errors.filter(e => e.status === 'resolved').length})
            </Button>
          </div>
        </div>

        <div className="divide-y max-h-[600px] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : filteredErrors.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No errors found</div>
          ) : (
            filteredErrors.map((error) => (
              <div key={error.id} className="p-4 hover:bg-muted/50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={error.severity === 'FATAL' || error.severity === 'PANIC' ? 'destructive' : 'secondary'}>
                        {error.severity}
                      </Badge>
                      <Badge variant="outline">{error.error_type}</Badge>
                      <span className="text-sm text-muted-foreground">{error.source}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(error.occurred_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="font-medium mb-1">{error.error_message}</p>
                    {error.error_details && (
                      <pre className="text-xs bg-muted p-2 rounded mt-2 overflow-x-auto">
                        {JSON.stringify(error.error_details, null, 2)}
                      </pre>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {error.status !== 'resolved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markAsResolved(error.id)}
                      >
                        <CheckIcon className="h-4 w-4 mr-1" />
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

export default function SystemMonitor() {
  const { isPlatformOwner, loading } = useAuth();
  
  if (loading) return null;
  if (!isPlatformOwner) return <Navigate to="/" replace />;

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Activity className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">System Monitor</h1>
            <p className="text-muted-foreground">
              Real-time monitoring for queue performance and system errors
            </p>
          </div>
        </div>

        <Tabs defaultValue="queue" className="space-y-6">
          <TabsList>
            <TabsTrigger value="queue">Queue Monitor</TabsTrigger>
            <TabsTrigger value="errors">Error Monitor</TabsTrigger>
          </TabsList>

          <TabsContent value="queue">
            <QueueMonitorTab />
          </TabsContent>

          <TabsContent value="errors">
            <ErrorMonitorTab />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
